/**
 * Vector Easements.com logo for PDF exports.
 *
 * Mirrors the site header lockup: an ink rounded square holding a scroll
 * glyph, followed by the "Easements.com" wordmark with brass accents. Drawn
 * with jsPDF primitives so it stays crisp at any zoom and needs no raster
 * asset (and no network fetch) inside export code paths.
 */
import type { jsPDF } from "jspdf";

const INK: [number, number, number] = [35, 47, 69];
const BRASS: [number, number, number] = [184, 133, 63];
const LIGHT: [number, number, number] = [240, 236, 228];

export interface BrandMarkOptions {
  /** Top-left corner of the logo tile. */
  x: number;
  y: number;
  /** Tile edge length in points. */
  size?: number;
  /** Draw the wordmark next to the tile. */
  withWordmark?: boolean;
  /** Use light text/tile for placement on a dark header band. */
  onDark?: boolean;
}

/** Draws the logo and returns the total width consumed. */
export function drawBrandMark(doc: jsPDF, opts: BrandMarkOptions): number {
  const size = opts.size ?? 26;
  const { x, y, onDark = false, withWordmark = true } = opts;

  // Tile
  if (onDark) {
    doc.setFillColor(...LIGHT);
  } else {
    doc.setFillColor(...INK);
  }
  doc.roundedRect(x, y, size, size, size * 0.22, size * 0.22, "F");

  // Scroll glyph: two horizontal rules between rolled ends.
  const glyph: [number, number, number] = onDark ? INK : LIGHT;
  doc.setDrawColor(...glyph);
  doc.setLineWidth(Math.max(0.7, size * 0.055));
  const pad = size * 0.24;
  const left = x + pad;
  const right = x + size - pad;
  doc.line(left, y + size * 0.38, right, y + size * 0.38);
  doc.line(left, y + size * 0.56, right - size * 0.12, y + size * 0.56);
  doc.setDrawColor(...BRASS);
  doc.line(left, y + size * 0.74, right - size * 0.24, y + size * 0.74);

  if (!withWordmark) return size;

  const textSize = size * 0.52;
  const baseline = y + size * 0.66;
  let cursor = x + size + size * 0.34;
  doc.setFont("helvetica", "bold").setFontSize(textSize);

  const segments: Array<[string, [number, number, number]]> = [
    ["Ease", BRASS],
    ["ments", onDark ? LIGHT : INK],
    [".com", BRASS],
  ];
  segments.forEach(([text, color]) => {
    doc.setTextColor(...color);
    doc.text(text, cursor, baseline);
    cursor += doc.getTextWidth(text);
  });

  return cursor - x;
}

/** Faint logo tile used inside the diagonal watermark lockup. */
export function drawWatermarkBrandMark(
  doc: jsPDF,
  x: number,
  y: number,
  size: number,
  angleDeg: number,
): void {
  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(Math.max(0.8, size * 0.06));
  const rad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  // Rotated square outline (y-down page: +x advances (cos, -sin)).
  const pt = (u: number, v: number): [number, number] => [
    x + u * cos + v * sin,
    y - u * sin + v * cos,
  ];
  const corners = [pt(-size / 2, -size / 2), pt(size / 2, -size / 2), pt(size / 2, size / 2), pt(-size / 2, size / 2)];
  for (let i = 0; i < corners.length; i += 1) {
    const a = corners[i]!;
    const b = corners[(i + 1) % corners.length]!;
    doc.line(a[0], a[1], b[0], b[1]);
  }
  // Scroll rules inside the tile.
  [-size * 0.16, 0, size * 0.16].forEach((v, i) => {
    const a = pt(-size * 0.26, v);
    const b = pt(size * 0.26 - i * size * 0.06, v);
    doc.line(a[0], a[1], b[0], b[1]);
  });
}
